import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AccessRightLevelEnum,
  EntitlementMode,
  GuardAccessRequirements,
} from '@osos/shared/types';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  CurrentUserPermissions,
  PermissionsState,
} from '../../state/permissions/permissions.state';

@Injectable({
  providedIn: 'root',
})
export class AccessPermissionService {
  currentUserPermission: CurrentUserPermissions;

  constructor(private store: Store) {}

  setCurrentUserPermission(currentUserPermission: CurrentUserPermissions) {
    this.currentUserPermission = currentUserPermission;
  }

  /**
   * userPermission map example
   * {
   *   system: 1,
   *   DataModule:439fe32b-4af0-4e01-b344-1fca663db877: 1
   *   DataEntity:439fe32b-4af0-4e01-b344-1fca663db877: 2
   *  }
   * This function use the userPermission Map to get the right top access for the given entity or module
   * @param accessRequirements
   * @returns Observable<boolean>
   */
  getRouteTopAccessLevel(
    accessRequirements: GuardAccessRequirements,
  ): Observable<AccessRightLevelEnum> {
    return this.store.select(PermissionsState.getCurrentUserPermissions).pipe(
      filter((permissionMap) => !!Object.keys(permissionMap).length),
      map((currentUserPermission: CurrentUserPermissions) => {
        // Set the current user permission as inner property so we can use it directly in directives that are used after call the guard:
        this.setCurrentUserPermission(currentUserPermission);
        switch (accessRequirements.access) {
          case EntitlementMode.System: {
            return this.getUserTopSystemLevel();
          }
          case EntitlementMode.DataModule: {
            return this.getUserTopModuleLevel(accessRequirements.moduleId);
          }
          case EntitlementMode.DataEntity: {
            return this.getUserTopEntityLevel(
              accessRequirements.moduleId,
              accessRequirements.entityId,
            );
          }
          case EntitlementMode.DataViews: {
            return this.getUserTopViewLevel(accessRequirements.dataViewId);
          }
          default: {
            return AccessRightLevelEnum.None;
          }
        }
      }),
    );
  }

  /**
   * For a given viewId, return the access right the user has in his accessPermissions
   * @param viewId
   * @returns AccessRightLevelEnum
   */
  getUserTopViewLevel(viewId: string): AccessRightLevelEnum {
    const viewMapKey = `${EntitlementMode.DataViews}:${viewId}`;
    return Object.keys(this.currentUserPermission).includes(viewMapKey)
      ? this.currentUserPermission[viewMapKey]
      : AccessRightLevelEnum.None;
  }

  getUserTopEntityLevel(moduleId: string, entityId): AccessRightLevelEnum {
    const entityMapKey = `${EntitlementMode.DataEntity}:${entityId}`;
    const moduleAccess = this.getUserTopModuleLevel(moduleId);

    return Object.keys(this.currentUserPermission).includes(entityMapKey)
      ? this.currentUserPermission[entityMapKey]
      : moduleAccess;
  }

  getUserTopModuleLevel(moduleId: string): AccessRightLevelEnum {
    const moduleMapKey = `${EntitlementMode.DataModule}:${moduleId}`;
    const systemAccess = this.getUserTopSystemLevel();

    return Object.keys(this.currentUserPermission).includes(moduleMapKey)
      ? this.currentUserPermission[moduleMapKey]
      : systemAccess;
  }

  getUserTopSystemLevel(): AccessRightLevelEnum {
    return Object.keys(this.currentUserPermission).includes(
      EntitlementMode.System,
    )
      ? this.currentUserPermission[EntitlementMode.System]
      : AccessRightLevelEnum.None;
  }

  hasEntityEditAccess(moduleId: string, entityId): boolean {
    const permission = this.getUserTopEntityLevel(moduleId, entityId);
    return permission >= AccessRightLevelEnum.EditAccess;
  }
  hasEntityDeveloperAccess(moduleId: string, entityId): boolean {
    const permission = this.getUserTopEntityLevel(moduleId, entityId);
    return permission >= AccessRightLevelEnum.DeveloperAccess;
  }
}
